import {
  DEFAULT_INVALIDATE_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  UseMutationCallbackType,
  UseQueryOptionsType,
} from "#hooks/adapters/adapterUtils";
import { CALCULATOR_RESULTS_QUERY_KEY } from "#hooks/adapters/useCalculatorResults";
import { getSuccessAndFailureCounts } from "#hooks/adapters/utils/bulkActionUtils";
import { useStickyState } from "#src/hooks/useStickyState";
import { useNavigate } from "#src/Routers/hooks";
import { RunLogRoutePath } from "#src/routes/data-tools/run-log";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useToast } from "@validereinc/common-components";
import {
  AssetTypeType,
  EstimationMethodAdapter,
  EstimationMethodApplyDefaultsAndRunCalculationsFilterType,
  EstimationMethodApplyDefaultsFilterType,
  EstimationMethodConfigurationKeyType,
  EstimationMethodConfigurationType,
  EstimationMethodInputType,
  EstimationMethodRunFilterType,
  EstimationMethodType,
  getFilterObject,
  isResponseErrorType,
  type EstimationMethodGetListRequestType,
  type EstimationMethodUpdateOneRequestType,
} from "@validereinc/domain";
import { downloadLink, HTTPResponseError } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import { produce } from "immer";
import React, { useMemo } from "react";
import { ExceptionUtils } from "../../../utils/exception";
import { getBulkActionToastDetails } from "../useBulkActionToast";

export const ESTIMATION_METHODS_QUERY_KEY = "estimationMethods";

export const useGetOneEstimationMethod = (
  params: {
    id: string;
    entityType: AssetTypeType;
  },
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof EstimationMethodAdapter.getOne>>
  > = {}
) =>
  useQuery({
    queryKey: [
      ESTIMATION_METHODS_QUERY_KEY,
      "getOne",
      params.id,
      { entityType: params.entityType },
    ] as const,
    queryFn: ({ queryKey: [_, __, id, meta] }) =>
      EstimationMethodAdapter.getOne({ id, meta }),
    enabled: !!params.id,
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useListEstimationMethods = (
  apiParams: Parameters<typeof EstimationMethodAdapter.getList>[0] = {}
) => {
  return useQuery({
    queryKey: [ESTIMATION_METHODS_QUERY_KEY, "list", apiParams],
    queryFn: () => EstimationMethodAdapter.getList(apiParams),
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useListEstimationMethodConfiguration = (
  apiParams: any,
  options: { enabled?: boolean; select?: (arg0: any) => any } = {}
) => {
  return useQuery({
    queryKey: [
      ESTIMATION_METHODS_QUERY_KEY,
      "configuration",
      "getList",
      apiParams,
    ],
    queryFn: () => EstimationMethodAdapter.configuration.getList(apiParams),
    ...{
      ...options,
      enabled: options.enabled && !!apiParams?.filters?.year_month?.length,
    }, // API requires a year month
  });
};

export const useListEstimationMethodRuns = (
  params: Parameters<typeof EstimationMethodAdapter.run.getList>[0] = {},
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof EstimationMethodAdapter.run.getList>>
  > = {}
) =>
  useQuery({
    queryKey: [ESTIMATION_METHODS_QUERY_KEY, "run", "getList", params] as const,
    queryFn: ({ queryKey: [_, __, ___, params] }) =>
      EstimationMethodAdapter.run.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useGetOneEstimationMethodConfiguration = (
  params: {
    id: string;
    assetType: AssetTypeType;
    yearMonth: string;
  },
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof EstimationMethodAdapter.configuration.getOne>>
  > = {}
) =>
  useQuery({
    queryKey: [
      ESTIMATION_METHODS_QUERY_KEY,
      "configuration",
      "getOne",
      params.id,
      { yearMonth: params.yearMonth, entityType: params.assetType },
    ] as const,
    queryFn: ({ queryKey: [_, __, ___, id, meta] }) =>
      EstimationMethodAdapter.configuration.getOne({ id, meta }),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useUpdateEstimationMethodConfiguration = ({
  onSuccess,
  onError,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof EstimationMethodAdapter.configuration.update>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearCalculationCache();

  return useMutation({
    mutationFn: ({
      id,
      assetType,
      yearMonth,
      data,
    }: {
      id: string;
      assetType: AssetTypeType;
      yearMonth: string;
      data: Partial<
        Omit<EstimationMethodConfigurationType, "inputs" | "asset_inputs"> & {
          inputs: Record<string, EstimationMethodInputType | null>;
          asset_inputs: Record<
            string,
            Record<string, EstimationMethodInputType | null> | null
          >;
        }
      >;
    }) =>
      EstimationMethodAdapter.configuration.update({
        id,
        data,
        meta: {
          entityType: assetType,
          yearMonth,
        },
      }),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      invalidate();
    },
    onError: (error, variables, context) => {
      onError?.(error, variables, context);
      toast.push({
        intent: "error",
        description: `Failed to update configuration: ${error instanceof HTTPResponseError ? (isResponseErrorType(error.responseBodyJson) ? error.responseBodyJson.error : JSON.stringify(error.responseBodyJson)) : error}`,
      });
    },
  });
};

export const useGetOneEstimationMethodRun = (
  params: {
    id: string;
    assetType: AssetTypeType;
    yearMonth: string;
  },
  options: UseQueryOptionsType<
    Awaited<ReturnType<typeof EstimationMethodAdapter.run.getOne>>
  > = {}
) =>
  useQuery({
    queryKey: [
      ESTIMATION_METHODS_QUERY_KEY,
      "run",
      "getOne",
      params.id,
      { yearMonth: params.yearMonth, entityType: params.assetType },
    ] as const,
    queryFn: ({ queryKey: [_, __, ___, id, meta] }) =>
      EstimationMethodAdapter.run.getOne({ id, meta }),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useRunEstimationMethod = () => {
  const { toast } = useToast();
  const { invalidate } = useClearCalculationCache();

  return useMutation({
    mutationFn: ({
      id,
      assetType,
      yearMonth,
      isPreview,
      promoteToRecord,
    }: {
      id: string;
      assetType: AssetTypeType;
      yearMonth: string;
      isPreview?: boolean;
      promoteToRecord?: boolean;
    }) =>
      EstimationMethodAdapter.run.create({
        meta: {
          methodId: id,
          entityType: assetType,
          yearMonth,
          isPreview,
          promoteToRecord,
        },
      }),
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: `Successfully ran calculation`,
      });
      invalidate();
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: `Failed to run calculation`,
      });
    },
  });
};

export const useBulkRunEstimationMethodsAsync = (
  jobKey: string,
  promoteToRecord = true
) => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  return useMutation({
    mutationFn: (filters: EstimationMethodRunFilterType) =>
      EstimationMethodAdapter.run.bulkRunAsync({
        filters,
        isPreview: false,
        promoteToRecord,
      }),
    onSuccess: (result) => {
      setJobId(result.job.id);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to run calculations",
      });
    },
  });
};

export const useApplyConfiguration = () => {
  const { toast } = useToast();
  const { invalidate } = useClearCalculationCache();

  return useMutation({
    mutationFn: ({
      id,
      assetType,
      yearMonth,
    }: {
      id: string;
      assetType: AssetTypeType;
      yearMonth: string;
      isPreview?: boolean;
    }) =>
      EstimationMethodAdapter.configuration.applyDefaults({
        id,
        meta: {
          entityType: assetType,
          yearMonth,
        },
      }),
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: `Successfully applied configuration`,
      });
      invalidate();
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: `Failed to apply configuration`,
      });
    },
  });
};

export const useBulkApplyConfigurationAsync = (jobKey: string) => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  return useMutation({
    mutationFn: (params: EstimationMethodApplyDefaultsFilterType) =>
      EstimationMethodAdapter.configuration.bulkApplyDefaultsAsync(params),
    onSuccess: (result) => {
      setJobId(result.job.id);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to apply configurations",
      });
    },
  });
};

export const useBulkApplyConfiguration = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { invalidate } = useClearCalculationCache();

  return useMutation({
    mutationFn: (ids: EstimationMethodConfigurationKeyType[]) =>
      EstimationMethodAdapter.configuration.bulkApplyDefaults(ids),
    onSuccess: ({ data }) => {
      const { successCount, failureCount, skippedCount, totalCount } =
        getSuccessAndFailureCounts(data);
      const { intent, description } = getBulkActionToastDetails({
        successCount,
        skippedCount,
        failureCount,
        totalCount,
        actionLabel: "Apply configurations",
      });
      toast.push({
        intent,
        description: (
          <>
            {description}{" "}
            <Link
              label="View Details"
              underline="always"
              onClick={() =>
                navigate({
                  pathname: RunLogRoutePath.path,
                })
              }
            />
          </>
        ),
      });
      invalidate();
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to apply configurations",
      });
    },
  });
};

export const useBulkApplyConfigurationsAndRunCalcsAsync = (jobKey: string) => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  return useMutation({
    mutationFn: (
      params: EstimationMethodApplyDefaultsAndRunCalculationsFilterType
    ) =>
      EstimationMethodAdapter.configuration.applyDefaultsAndRunCalculationsAsync(
        params
      ),
    onSuccess: (result) => {
      setJobId(result.job.id);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to run calculations",
      });
    },
  });
};

export const useBulkRunEstimationMethods = (promoteToRecord = true) => {
  const { toast } = useToast();
  const { invalidate } = useClearCalculationCache();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (filters: EstimationMethodRunFilterType) =>
      EstimationMethodAdapter.run.bulkRun({
        filters,
        isPreview: false,
        promoteToRecord,
      }),
    onSuccess: ({ data }) => {
      const { successCount, failureCount, skippedCount, totalCount } =
        getSuccessAndFailureCounts(data);
      const { intent, description } = getBulkActionToastDetails({
        successCount,
        skippedCount,
        failureCount,
        totalCount,
        actionLabel: "Run calculations",
      });
      toast.push({
        intent,
        description: (
          <>
            {description}{" "}
            <Link
              label="View Details"
              underline="always"
              onClick={() =>
                navigate({
                  pathname: RunLogRoutePath.path,
                })
              }
            />
          </>
        ),
      });
      invalidate();
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to run calculations",
      });
    },
  });
};

export const useExportEstimationMethods = (
  apiParams: Parameters<typeof EstimationMethodAdapter.exportList>[0]
) => {
  return useMutation({
    mutationFn: async () => {
      const { name, s3_download_link } =
        await EstimationMethodAdapter.exportList(apiParams);

      downloadLink(s3_download_link, name);
    },
  });
};

export const useUpdateEstimationMethod = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof EstimationMethodAdapter.updateOne>>
> = {}) => {
  const { invalidate } = useClearCalculationCache();

  return useMutation({
    mutationFn: async (
      params: Parameters<typeof EstimationMethodAdapter.updateOne>[0]
    ) => EstimationMethodAdapter.updateOne(params).then((resp) => resp.data),
    onSuccess: (data, variables, context) => {
      // @ts-expect-error TODO: fix filter typing
      onSuccess?.(data, variables, context);
      invalidate();
    },
  });
};

export const useUpdateEstimationMethodsAsync = ({
  onSuccess,
  onError,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof EstimationMethodAdapter.updateAsync>>
> = {}) => {
  return useMutation({
    mutationFn: async ({
      filter,
      data,
      mergeDefaultAssetValues,
    }: {
      filter: EstimationMethodGetListRequestType["filters"] &
        Record<string, string>;
      mergeDefaultAssetValues: boolean;
      data: EstimationMethodUpdateOneRequestType["data"];
    }) =>
      EstimationMethodAdapter.updateAsync({
        data: {
          update: data,
          merge_default_asset_values: mergeDefaultAssetValues,
        },
        meta: {
          filter,
        },
      }),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      onError?.(error, variables, context);
    },
  });
};

export const useBulkUpdateEstimationMethodStatus = ({
  jobKey,
  filters,
  selectedEstimationMethods,
  isSelectionInverted,
  setIsUpdating,
  invalidate,
}: {
  jobKey: string;
  filters: EstimationMethodGetListRequestType["filters"] & Record<string, any>;
  selectedEstimationMethods: Record<string, EstimationMethodType>;
  isSelectionInverted: boolean;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  invalidate: () => void;
}): {
  handleBulkUpdateEstimationMethodStatus: (
    update: EstimationMethodUpdateOneRequestType["data"]
  ) => Promise<void>;
} => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  const bulkUpdateEstimationMethodStatusFilters = useMemo(() => {
    if (isSelectionInverted) {
      return filters;
    }

    return produce(filters, (draft) => {
      draft.filters = getFilterObject(filters);
      draft.filters.isAlreadyFormatted = true;

      if (!isEmpty(selectedEstimationMethods)) {
        draft.filters.id = {
          $in: Object.keys(selectedEstimationMethods),
        };
      }
    }).filters;
  }, [filters, JSON.stringify(selectedEstimationMethods), isSelectionInverted]);

  const mutation = useUpdateEstimationMethodsAsync({
    onSuccess: (data) => {
      setJobId(data.job.id);
      invalidate();
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to bulk update estimation method status",
      });
    },
  });

  const handleBulkUpdateEstimationMethodStatus = async (
    update: EstimationMethodUpdateOneRequestType["data"]
  ) => {
    setIsUpdating(true);
    await mutation.mutateAsync({
      data: {
        ...update,
      },
      filter: bulkUpdateEstimationMethodStatusFilters,
      mergeDefaultAssetValues: true,
    });
    setIsUpdating(false);
  };

  return {
    handleBulkUpdateEstimationMethodStatus,
  };
};

export const useClearCalculationCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [ESTIMATION_METHODS_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
      queryClient.invalidateQueries({
        queryKey: [CALCULATOR_RESULTS_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};
